import { useRef, useState } from "react";
import datePickerImage from "../../../assets/images/calender.svg";
import useLanguageTranslate from "../../../hooks/useLanguageTranslate";
import "./Input.css";

const Input = ({
  inputClassName = "",
  label,
  name,
  value,
  handleChange,
  error,
  type = "text",
  placeHolder = "",
  disabled,
  setFormData,
  startAdornment,
  startAdornmentClassName,
  startAdornmentInputClassName = "",
  endAdornment,
  isTextarea = false, // New prop to determine if it's a textarea
  hint_msg = null,
  className = "",
  formatNumber = false, // New prop to enable number formatting
  ...rest
}) => {
  const translate = useLanguageTranslate();
  const [isFocused, setIsFocused] = useState(false);
  const ref = useRef(null);

  const handleFocus = () => setIsFocused(true);
  const handleBlur = (e) => {
    setIsFocused(false);
    // Call custom onBlur if provided via rest props
    if (rest.onBlur) {
      rest.onBlur(e);
    }
  };
  const datePickerClick = () => {
    if (type === "date" && ref?.current) {
      ref.current.showPicker();
    }
  };

  // Convert dd/mm/yyyy to yyyy-mm-dd for date input
  const formatDateForInput = (dateValue) => {
    if (!dateValue || type !== "date") return dateValue;
    try {
      // If already in yyyy-mm-dd format, return as is
      if (dateValue.includes("-") && dateValue.length === 10) return dateValue;

      // If in dd/mm/yyyy format, convert to yyyy-mm-dd
      if (dateValue.includes("/")) {
        const [day, month, year] = dateValue.split("/");
        return `${year}-${month.padStart(2, "0")}-${day.padStart(2, "0")}`;
      }

      return dateValue;
    } catch {
      return dateValue;
    }
  };

  // Format date from yyyy-mm-dd to dd/mm/yyyy for display
  const formatDateForDisplay = (dateValue) => {
    if (!dateValue || type !== "date") return dateValue;
    try {
      // If value is in dd/mm/yyyy format, return as is
      if (dateValue.includes("/")) return dateValue;

      // If value is in yyyy-mm-dd format (from date input), convert to dd/mm/yyyy
      if (dateValue.includes("-") && dateValue.length === 10) {
        const [year, month, day] = dateValue.split("-");
        return `${day}/${month}/${year}`;
      }

      return dateValue;
    } catch {
      return dateValue;
    }
  };

  // Handle date change with proper formatting
  const handleDateChange = (e) => {
    if (type === "date") {
      const inputValue = e.target.value; // This will be in yyyy-mm-dd format
      if (inputValue) {
        // Convert yyyy-mm-dd to dd/mm/yyyy for the parent component
        const formattedValue = formatDateForDisplay(inputValue);

        // Create synthetic event with formatted value
        const syntheticEvent = {
          ...e,
          target: {
            ...e.target,
            value: formattedValue,
          },
        };
        handleChange(syntheticEvent);
      } else {
        handleChange(e);
      }
    } else {
      handleChange(e);
    }
  };

  // Handle number formatting
  const handleNumberChange = (e) => {
    if (formatNumber) {
      const inputValue = e.target.value;
      // Remove any non-digit characters (including spaces and commas)
      const cleanValue = inputValue.replace(/[^\d]/g, "");
      let formattedValue = cleanValue;

      if (cleanValue && !isNaN(cleanValue)) {
        // Always use en-US locale to ensure consistent comma formatting for all users
        formattedValue = Number(cleanValue).toLocaleString('en-US');
      }

      // Create synthetic event with formatted value
      const syntheticEvent = {
        ...e,
        target: {
          ...e.target,
          value: formattedValue,
        },
      };
      handleChange(syntheticEvent);
    } else {
      handleChange(e);
    }
  };

  return (
    <div
      // className="formField form-input"
      className={`formField form-input ${inputClassName}`}
    >
      {label && (
        <label className="label text-[#48535B] text-base not-italic font-normal leading-[26px] tracking-[-0.16px]">
          {label}
        </label>
      )}
      <div className="w-full relative">
        {isTextarea ? (
          <textarea
            name={name}
            placeholder={placeHolder}
            value={value}
            onChange={handleChange}
            onFocus={handleFocus}
            onBlur={handleBlur}
            disabled={!!disabled}
            className={`${endAdornment && "endAdornmentInput"} ${
              startAdornment && "startAdornmentInput"
            } w-full flex justify-center items-center gap-2.5 self-stretch py-3 border rounded-[10px] border-solid border-[#DDE2E4] text-black text-lg not-italic font-normal leading-[26px] tracking-[-0.18px] h-[200px] resize-none mb-6 ${
              isFocused
                ? "border-active border-2 rounded border-[#000000]"
                : "border-default border text-[#DDE2E4] text-lg not-italic font-medium leading-[26px] tracking-[-0.18px] rounded-[10px]"
            } ${className}`}
            rows="4" // Set the number of rows for textarea
            {...rest}
          />
        ) : type === "date" ? (
          <div className="relative w-full">
            {/* Hidden date input for native picker functionality */}
            <input
              ref={ref}
              type="date"
              name={name}
              value={formatDateForInput(value)}
              onChange={handleDateChange}
              onFocus={handleFocus}
              onBlur={handleBlur}
              disabled={!!disabled}
              className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
              {...rest}
            />
            {/* Visible text input showing formatted date */}
            <input
              type="text"
              value={value ? formatDateForDisplay(value) : ""}
              placeholder={placeHolder || "dd/mm/yyyy"}
              readOnly
              disabled={!!disabled}
              className={`${endAdornment && "endAdornmentInput"} ${
                startAdornment && "startAdornmentInput"
              } w-full h-[50px] justify-center min-h-[50px] items-center gap-2.5 self-stretch px-4 py-3 text-lg not-italic font-normal border rounded-[10px] border-solid border-[#B0BABF] ${
                isFocused
                  ? "border-active h-[50px] text-lg"
                  : "border-default border text-lg not-italic font-medium leading-[26px] rounded-[10px] h-[50px]"
              } ${className} cursor-pointer bg-white`}
            />
          </div>
        ) : (
          <input
            ref={ref}
            type={type}
            name={name}
            value={value}
            placeholder={placeHolder}
            onChange={formatNumber ? handleNumberChange : handleChange}
            onFocus={handleFocus}
            onBlur={handleBlur}
            disabled={!!disabled}
            className={`${endAdornment && "endAdornmentInput"} ${
              startAdornment && "startAdornmentInput"
            } ${startAdornmentInputClassName} w-full h-[50px] justify-center min-h-[50px] items-center gap-2.5 self-stretch px-4 py-3 text-lg not-italic font-normal border rounded-[10px] border-solid border-[#DDE2E4] ${
              isFocused
                ? "border-active h-[50px] text-lg"
                : "border-default border text-lg not-italic font-medium leading-[26px] rounded-[10px] h-[50px]"
            } ${className}`}
            {...rest}
          />
        )}
        {startAdornment && (
          <span className={`startAdornment ${startAdornmentClassName} `}>
            {startAdornment}
          </span>
        )}
        {endAdornment && <span className="endAdornment">{endAdornment}</span>}
        {type == "date" && (
          <span
            onClick={datePickerClick}
            className="endAdornment datePickerIcon"
          >
            <img src={datePickerImage} alt="" />
          </span>
        )}
      </div>
      {hint_msg && <small>{hint_msg}</small>}
      {error && (
        <p className="field-err text-red-500 text-sm mt-1 text-left rtl:text-right">
          {error}
        </p>
      )}
    </div>
  );
};

export default Input;
